// html2canvas and jspdf are heavy (~400 KB combined); load them only when an
// export is actually triggered so they stay out of the route chunks.

interface ExportOptions {
  filename: string
  themeAware?: boolean // capture in light mode for legibility even if user is in dark mode
}

/**
 * Capture a DOM element to a PNG and trigger a browser download.
 * Useful for shareable social cards.
 */
export async function exportElementToPng(el: HTMLElement, opts: ExportOptions): Promise<void> {
  const { default: html2canvas } = await import('html2canvas')
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: null, // preserve transparency / gradient
    logging: false,
    windowWidth: el.scrollWidth,
  })
  const url = canvas.toDataURL('image/png')
  const a = document.createElement('a')
  a.href = url
  a.download = opts.filename
  a.click()
}

/**
 * Render a DOM element to a multi-page PDF.
 * Pipeline: html2canvas → one tall canvas → slice into per-page canvases → jsPDF A4.
 * Forces light mode during capture so PDFs are readable when printed/shared.
 */
export async function exportElementToPdf(el: HTMLElement, opts: ExportOptions): Promise<void> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ])
  const root = document.documentElement
  const wasDark = root.classList.contains('dark')
  const themeAware = opts.themeAware ?? true
  if (themeAware && wasDark) root.classList.remove('dark')

  // Give the DOM one paint to apply light-mode CSS vars before snapshotting.
  await new Promise((r) => requestAnimationFrame(() => r(null)))
  await new Promise((r) => setTimeout(r, 50))

  let canvas: HTMLCanvasElement
  try {
    canvas = await html2canvas(el, {
      scale: Math.min(2, window.devicePixelRatio || 1.5),
      useCORS: true,
      backgroundColor: '#FFFBF0', // match Neo-Brutalist canvas bg
      logging: false,
      windowWidth: el.scrollWidth,
    })
  } finally {
    if (themeAware && wasDark) root.classList.add('dark')
  }

  const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 24
  const usableWidth = pageWidth - margin * 2
  const usableHeight = pageHeight - margin * 2

  // Scale source canvas px → PDF pt
  const ptPerPx = usableWidth / canvas.width
  // How many source pixels fit in one PDF page height
  const sliceHeightPx = Math.floor(usableHeight / ptPerPx)

  let cursorPx = 0
  let pageIndex = 0
  while (cursorPx < canvas.height) {
    if (pageIndex > 0) pdf.addPage()
    const thisSlicePx = Math.min(sliceHeightPx, canvas.height - cursorPx)

    const sliceCanvas = document.createElement('canvas')
    sliceCanvas.width = canvas.width
    sliceCanvas.height = thisSlicePx
    const ctx = sliceCanvas.getContext('2d')!
    ctx.fillStyle = '#FFFBF0'
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height)
    ctx.drawImage(canvas, 0, -cursorPx)

    pdf.addImage(
      sliceCanvas.toDataURL('image/png'),
      'PNG',
      margin,
      margin,
      usableWidth,
      thisSlicePx * ptPerPx,
      undefined,
      'FAST',
    )
    cursorPx += thisSlicePx
    pageIndex += 1
  }

  pdf.save(opts.filename)
}
